import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { doc, collection, query, where, onSnapshot, updateDoc } from 'firebase/firestore';
import { ref, getBlob } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { Document, Page, pdfjs } from 'react-pdf';
import { useAuth } from '../context/AuthContext';
import { usePostHog } from '@posthog/react';
import { analyzeDocument, processDocument, processCategoriesDetection, updatePiiItemStatus, searchPiiInDocument, addManualPiiRedaction } from '../services/cloudFunctions';
import DashboardLayout from '../components/layout/DashboardLayout';
import DocumentAnalysisModal from '../components/documents/DocumentAnalysisModal';
import DetectionWizardModal from '../components/documents/DetectionWizardModal';
import CategoriesSidebar, { getCategoryColor } from '../components/documents/CategoriesSidebar';
import DownloadSettingsModal from '../components/documents/DownloadSettingsModal';
import PasswordPromptModal from '../components/documents/PasswordPromptModal';
import ManualRedactionToolbar from '../components/documents/ManualRedactionToolbar';
import { ArrowLeft, FileText, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Loader, Eye, EyeOff, Wand2, AlertCircle } from 'lucide-react';
import ProcessingStatus from '../components/documents/ProcessingStatus';

// Set up PDF.js worker - using CDN to avoid build path resolution issues
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;

// Color mapping for different PII categories
const PII_COLORS = {
  // Standard singular forms
  NAME: { bg: 'rgba(255, 193, 7, 0.3)', border: '#ffc107' },
  EMAIL: { bg: 'rgba(33, 150, 243, 0.3)', border: '#2196f3' },
  PHONE: { bg: 'rgba(156, 39, 176, 0.3)', border: '#9c27b0' },
  ID_NUMBER: { bg: 'rgba(244, 67, 54, 0.3)', border: '#f44336' },
  ADDRESS: { bg: 'rgba(76, 175, 80, 0.3)', border: '#4caf50' },
  DATE_OF_BIRTH: { bg: 'rgba(255, 152, 0, 0.3)', border: '#ff9800' },
  FINANCIAL: { bg: 'rgba(233, 30, 99, 0.3)', border: '#e91e63' },

  // Plural forms (map to same colors as singular)
  NAMES: { bg: 'rgba(255, 193, 7, 0.3)', border: '#ffc107' },
  EMAILS: { bg: 'rgba(33, 150, 243, 0.3)', border: '#2196f3' },
  ADDRESSES: { bg: 'rgba(76, 175, 80, 0.3)', border: '#4caf50' },

  // Generic identifier types (assign distinct colors)
  IDENTIFIERS: { bg: 'rgba(103, 58, 183, 0.3)', border: '#673ab7' }, // Deep Purple
  IDENTIFIER: { bg: 'rgba(103, 58, 183, 0.3)', border: '#673ab7' },

  // Manual redactions
  MANUAL: { bg: 'rgba(59, 130, 246, 0.3)', border: '#3b82f6' },

  // Backward compatibility for legacy data
  SSN: { bg: 'rgba(244, 67, 54, 0.3)', border: '#f44336' },

  DEFAULT: { bg: 'rgba(158, 158, 158, 0.3)', border: '#9e9e9e' }
};

// Context badge styling for PII context types
const CONTEXT_COLORS = {
  person_specific: { classes: 'bg-red-100 text-red-700', label: 'Person' },
  juristic_person: { classes: 'bg-amber-100 text-amber-700', label: 'Company' },
  professional_capacity: { classes: 'bg-sky-100 text-sky-700', label: 'Professional' },
  patient_finding: { classes: 'bg-rose-100 text-rose-700', label: 'Patient Data' },
  methodology_reference: { classes: 'bg-gray-100 text-gray-600', label: 'Reference' },
  disease_description: { classes: 'bg-gray-100 text-gray-600', label: 'Disease Info' },
  literature_reference: { classes: 'bg-gray-100 text-gray-600', label: 'Literature' },
};

function getContextBadge(context) {
  if (!context) return null;
  const entry = CONTEXT_COLORS[context];
  if (entry) {
    return { classes: entry.classes, label: entry.label };
  }
  // Fallback: format the key as a readable label
  const label = context.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return { classes: 'bg-gray-100 text-gray-600', label };
}

function PiiGroupedItem({ group, getPiiColor, hoveredPii, setHoveredPii, onDeleteAll, onDeleteSingle, setCurrentPage, currentPage }) {
  const [expanded, setExpanded] = useState(false);
  const firstItem = group.items[0];
  const color = getPiiColor(firstItem.category);
  const pages = [...new Set(group.items.map(i => i.page || 1))].sort((a, b) => a - b);
  const pageLabel = pages.length === 1 ? `Page ${pages[0]}` : `Pages ${pages[0]}-${pages[pages.length - 1]}`;
  const contextBadge = getContextBadge(firstItem.context);

  return (
    <div className="border-b border-gray-100">
      <div
        className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(prev => !prev)}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs text-gray-400 select-none">{expanded ? '▾' : '▸'}</span>
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: color.border }}
          />
          <span className="text-xs font-medium text-gray-500 uppercase truncate">
            {firstItem.category}
          </span>
          <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
            {group.items.length}x
          </span>
          {contextBadge && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${contextBadge.classes}`}>
              {contextBadge.label}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-900 font-mono break-all ml-5">
          {firstItem.value}
        </p>
        <div className="flex items-center justify-between mt-2 ml-5">
          <span className="text-xs text-gray-400">{pageLabel}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteAll(group.items);
            }}
            className="text-xs text-red-600 hover:text-red-800 hover:underline"
          >
            Keep All
          </button>
        </div>
      </div>
      {expanded && (
        <div className="divide-y divide-gray-50 bg-gray-50/50">
          {group.items.map((item) => (
            <PiiSidebarItem
              key={item.id}
              item={item}
              color={getPiiColor(item.category)}
              hoveredPii={hoveredPii}
              setHoveredPii={setHoveredPii}
              onDelete={onDeleteSingle}
              onClick={() => setCurrentPage(item.page || 1)}
              showPageNav={(item.page || 1) !== currentPage}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PiiSidebarItem({ item, color, hoveredPii, setHoveredPii, onDelete, onClick, showPageNav }) {
  return (
    <div
      className={`p-3 pr-8 relative transition-colors ${
        hoveredPii === item.id ? 'bg-gray-100' : 'hover:bg-gray-50'
      }`}
    >
      <div
        className="cursor-pointer"
        onMouseEnter={() => setHoveredPii(item.id)}
        onMouseLeave={() => setHoveredPii(null)}
        onClick={onClick}
      >
        <div className="flex items-center gap-2 mb-1">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: color.border }}
          />
          <span className="text-xs font-medium text-gray-500 uppercase">
            {item.category === 'MANUAL' ? 'MANUAL REDACTION' : item.category}
          </span>

          {item.detectionType === 'keyword' && (
            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
              Keyword
            </span>
          )}
          {item.detectionType === 'manual' && (
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              Manual
            </span>
          )}

          {item.confidence && (
            <span className="text-xs text-gray-400 ml-auto">
              {Math.round(item.confidence * 100)}%
            </span>
          )}
          {(() => {
            const badge = getContextBadge(item.context);
            return badge ? (
              <span className={`text-xs px-1.5 py-0.5 rounded ${badge.classes}`}>
                {badge.label}
              </span>
            ) : null;
          })()}
        </div>
        <p className="text-sm text-gray-900 font-mono break-all pr-6">
          {item.detectionType === 'keyword' ? item.searchKeyword : item.value}
        </p>
        {showPageNav && (
          <p className="text-xs text-emerald-600 mt-1">Go to page {item.page || 1}</p>
        )}
      </div>
      <button
        onClick={() => onDelete(item.id)}
        className="absolute top-2 right-2 p-1 rounded hover:bg-red-100 transition-colors group"
        title="Remove this PII item"
      >
        <X className="w-4 h-4 text-gray-400 group-hover:text-red-600" />
      </button>
    </div>
  );
}

export default function DocumentView() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const posthog = usePostHog();
  const [documentData, setDocumentData] = useState(null);
  const [piiItems, setPiiItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const [hoveredPii, setHoveredPii] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showDetectionWizard, setShowDetectionWizard] = useState(false);
  const [isCategoriesProcessing, setIsCategoriesProcessing] = useState(false);
  const [activeMethod, setActiveMethod] = useState(null); // 'manual' | 'popia' | 'categories' | null
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  const autoAnalyzeTriggered = useRef(false);
  const wizardAutoOpened = useRef(false);
  const [pendingPopia, setPendingPopia] = useState(false);
  const pdfPasswordCallbackRef = useRef(null);

  // Manual redaction state
  const [drawMode, setDrawMode] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showAllPages, setShowAllPages] = useState(false);
  const [redactionPreview, setRedactionPreview] = useState(false);

  // Real-time listeners for document and PII items
  useEffect(() => {
    if (!documentId || !currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let pdfBlobUrl = null;
    let pdfLoaded = false;

    // Real-time listener for document
    const docRef = doc(db, 'documents', documentId);
    const unsubscribeDoc = onSnapshot(docRef, async (docSnap) => {
      if (docSnap.exists()) {
        const docData = { id: docSnap.id, ...docSnap.data() };
        setDocumentData(docData);

        // When status changes to 'processed', stop showing processing spinner
        if (docData.status === 'processed') {
          setIsProcessing(false);
        }
        if (docData.status === 'error') {
          setIsProcessing(false);
          setIsAnalyzing(false);
        }

        // Load PDF only once (on first snapshot)
        if (!pdfLoaded && docData.storagePath) {
          pdfLoaded = true;
          try {
            setPdfLoading(true);
            const storageRef = ref(storage, docData.storagePath);
            const blob = await getBlob(storageRef);
            pdfBlobUrl = URL.createObjectURL(blob);
            setPdfFile(pdfBlobUrl);
          } catch (pdfErr) {
            console.error('Error loading PDF:', pdfErr);
            setPdfError('Failed to load PDF file');
          } finally {
            setPdfLoading(false);
          }
        }
      } else {
        console.error('Document not found');
        navigate('/dashboard');
      }
      setLoading(false);
    }, (error) => {
      console.error('Error listening to document:', error);
      setLoading(false);
    });

    // Real-time listener for PII items
    const piiQuery = query(
      collection(db, 'piiItems'),
      where('documentId', '==', documentId),
      where('userId', '==', currentUser.uid),
      where('status', 'in', ['detected', 'redacted', 'selected'])
    );
    const unsubscribePii = onSnapshot(piiQuery, (piiSnap) => {
      const items = piiSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPiiItems(items);
    }, (error) => {
      console.error('Error listening to PII items:', error);
    });

    // Cleanup
    return () => {
      unsubscribeDoc();
      unsubscribePii();
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [documentId, navigate, currentUser]);

  // Reset refs and activeMethod when navigating to a different document
  useEffect(() => {
    autoAnalyzeTriggered.current = false;
    wizardAutoOpened.current = false;
    setActiveMethod(null);
  }, [documentId]);

  // Auto-analyze document type (free, no credits) for freshly uploaded docs
  useEffect(() => {
    if (documentData?.status === 'uploaded' && !autoAnalyzeTriggered.current) {
      autoAnalyzeTriggered.current = true;
      setIsAnalyzing(true);
      analyzeDocument(documentId)
        .then(result => {
          if (!result.success) {
            if (result.requiresPassword) {
              setShowPasswordPrompt(true);
            } else {
              console.error('Auto-analyze returned error:', result.error);
            }
          }
        })
        .catch(err => console.error('Auto-analyze failed:', err))
        .finally(() => setIsAnalyzing(false));
    }
  }, [documentData?.status, documentId]);

  // Auto-open wizard once analysis is complete and document is ready for detection
  useEffect(() => {
    if (!documentData || wizardAutoOpened.current) return;
    const status = documentData.status;
    if (status === 'analyzed') {
      wizardAutoOpened.current = true;
      setShowDetectionWizard(true);
    }
  }, [documentData]);

  // Effective method: user selection wins, then Firestore, then status-based default
  const effectiveMethod = activeMethod ?? documentData?.activeMethod ??
    (documentData?.status === 'processed' ? 'popia' : null);

  // When user selected POPIA while auto-analyze was running, show modal once ready
  useEffect(() => {
    if (pendingPopia && documentData?.analysis && !isAnalyzing) {
      setPendingPopia(false);
      setAnalysisResult(documentData.analysis);
      setShowAnalysisModal(true);
    }
  }, [pendingPopia, documentData?.analysis, isAnalyzing]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page) => {
    setPageDimensions({
      width: page.originalWidth,
      height: page.originalHeight
    });
  };

  const goToPrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, numPages || 1));
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 2.5));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleDownload = () => {
    // Open the download settings modal
    setShowDownloadModal(true);
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    try {
      const result = await analyzeDocument(documentId);

      if (result.success) {
        setAnalysisResult(result.analysis);
        setShowAnalysisModal(true);
      } else if (result.requiresPassword) {
        setShowPasswordPrompt(true);
      } else {
        console.error('Analyze error:', result.error);
        alert('Failed to analyze document. Please try again.');
      }
    } catch (error) {
      console.error('Error analyzing document:', error);
      alert('Failed to analyze document. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handlePasswordSubmit = async (password) => {
    setIsUnlocking(true);
    setPasswordError('');
    try {
      const result = await analyzeDocument(documentId, password);

      if (result.success) {
        setShowPasswordPrompt(false);
        setPasswordError('');

        // If react-pdf is waiting for the password, provide it
        if (pdfPasswordCallbackRef.current) {
          pdfPasswordCallbackRef.current(password);
          pdfPasswordCallbackRef.current = null;
        }

        // Backend has replaced the encrypted PDF with a decrypted version.
        // Reload the PDF from Storage so react-pdf won't need a password next time.
        if (documentData?.storagePath) {
          try {
            const storageRef = ref(storage, documentData.storagePath);
            const blob = await getBlob(storageRef);
            const newBlobUrl = URL.createObjectURL(blob);
            if (pdfFile) URL.revokeObjectURL(pdfFile);
            setPdfFile(newBlobUrl);
            pdfPasswordCallbackRef.current = null;
          } catch (e) {
            console.error('Failed to reload decrypted PDF:', e);
          }
        }
      } else if (result.requiresPassword) {
        setPasswordError(result.error || 'Incorrect password. Please try again.');
      } else {
        setShowPasswordPrompt(false);
        console.error('Analyze error:', result.error);
        alert('Failed to analyze document. Please try again.');
      }
    } catch (error) {
      console.error('Error unlocking document:', error);
      setPasswordError('Failed to unlock document. Please try again.');
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleConfirmProcess = async ({ domainType }) => {
    setIsProcessing(true);
    setShowAnalysisModal(false);

    try {
      const result = await processDocument(documentId, domainType);

      if (!result.success) {
        console.error('Process error:', result.error);
        alert('Failed to process document. Please try again.');
        setIsProcessing(false);
      }
      // Don't reload - real-time listeners will update UI automatically
      // isProcessing will be set to false when document status changes to 'processed'
    } catch (error) {
      console.error('Error processing document:', error);
      alert('Failed to process document. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleWizardSelect = async (method) => {
    setShowDetectionWizard(false);
    setActiveMethod(method);

    // Persist activeMethod to Firestore so it survives page reload
    const docRef = doc(db, 'documents', documentId);
    updateDoc(docRef, { activeMethod: method }).catch(err =>
      console.error('Failed to persist activeMethod:', err)
    );

    if (method === 'manual') {
      // Manual mode — nothing to do, just close wizard
      return;
    }

    if (method === 'popia') {
      if (documentData.analysis) {
        // Already analyzed (from auto-analyze) — show modal directly
        setAnalysisResult(documentData.analysis);
        setShowAnalysisModal(true);
      } else if (isAnalyzing) {
        // Auto-analyze running — show modal when it completes
        setPendingPopia(true);
      } else {
        handleAnalyze();
      }
      return;
    }

    if (method === 'categories') {
      setIsCategoriesProcessing(true);
      try {
        const result = await processCategoriesDetection(documentId);
        if (!result.success) {
          console.error('Categories detection error:', result.error);
          alert('Categories detection failed. Please try again.');
        }
        // Real-time listeners will update UI automatically
      } catch (error) {
        console.error('Categories detection error:', error);
        alert('Failed to run categories detection. Please try again.');
      } finally {
        setIsCategoriesProcessing(false);
      }
    }
  };

  const handleDeletePii = async (piiItemId, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Remove this PII item? It will no longer be redacted.')) return;

    try {
      const result = await updatePiiItemStatus(piiItemId, 'approved');
      if (!result.success) {
        console.error('PII status update error:', result.error);
        alert('Failed to remove PII item. Please try again.');
      } else if (currentUser && posthog) {
        posthog.capture('pii_item_status_updated', {
          document_id: documentId,
          pii_item_id: piiItemId,
          new_status: 'approved',
        });
      }
      // Real-time listener will update UI automatically
    } catch (error) {
      console.error('Error removing PII:', error);
      posthog?.captureException(error);
      alert('Failed to remove PII item.');
    }
  };

  const handleKeepAll = async (items) => {
    if (!window.confirm(`Remove ${items.length} PII item${items.length !== 1 ? 's' : ''} from redaction? They will no longer be redacted.`)) return;
    await Promise.all(items.map(item => handleDeletePii(item.id, true)));
  };

  const handleKeywordSearch = async (keyword) => {
    setIsSearching(true);
    try {
      const result = await searchPiiInDocument(documentId, {
        keyword
      });

      if (result.success) {
        if (result.count === 0) {
          alert(`No occurrences of "${keyword}" found in the document.`);
        } else {
          alert(`Added ${result.count} redaction(s) for "${keyword}"`);
        }
      } else {
        console.error('Keyword search error:', result.error);
        alert('Search failed. Please try again.');
      }
    } catch (error) {
      console.error('Keyword search error:', error);
      alert('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handlePointerDown = (e) => {
    if (!drawMode) return;
    if (pageDimensions.width === 0) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / (pageDimensions.width * scale);
    const y = (e.clientY - rect.top) / (pageDimensions.height * scale);

    setDrawStart({ x, y });
    setIsDrawing(true);
  };

  const handlePointerMove = (e) => {
    if (!drawMode || !isDrawing) return;
    if (pageDimensions.width === 0) return;

    e.preventDefault();

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / (pageDimensions.width * scale);
    const y = (e.clientY - rect.top) / (pageDimensions.height * scale);

    setDrawCurrent({ x, y });
  };

  const handlePointerUp = async (e) => {
    if (!drawMode || !isDrawing || !drawStart) return;
    if (pageDimensions.width === 0) return;

    e.currentTarget.releasePointerCapture(e.pointerId);

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / (pageDimensions.width * scale);
    const y = (e.clientY - rect.top) / (pageDimensions.height * scale);

    // Calculate bounding box (normalized 0-1)
    const box = {
      x0: Math.min(drawStart.x, x),
      y0: Math.min(drawStart.y, y),
      x1: Math.max(drawStart.x, x),
      y1: Math.max(drawStart.y, y)
    };

    // Validate minimum size (1% of page)
    const minSize = 0.01;
    if ((box.x1 - box.x0) < minSize || (box.y1 - box.y0) < minSize) {
      setIsDrawing(false);
      setDrawStart(null);
      setDrawCurrent(null);
      return;
    }

    // Save manual redaction
    try {
      const result = await addManualPiiRedaction(documentId, {
        page: currentPage,
        boundingBox: box
      });

      if (!result.success) {
        console.error('Manual redaction error:', result.error);
        alert('Failed to add redaction. Please try again.');
      } else if (currentUser && posthog) {
        posthog.capture('manual_redaction_added', {
          document_id: documentId,
          page: currentPage,
        });
      }
    } catch (error) {
      console.error('Error adding manual redaction:', error);
      posthog?.captureException(error);
      alert('Failed to add manual redaction.');
    }

    // Reset drawing state
    setIsDrawing(false);
    setDrawStart(null);
    setDrawCurrent(null);
  };

  // Filter PII items based on active detection method
  const filteredPiiItems = (() => {
    if (effectiveMethod === 'manual') {
      return piiItems.filter(item =>
        item.detectionMethod === 'manual' ||
        (!item.detectionMethod && (item.detectionType === 'manual' || item.detectionType === 'keyword'))
      );
    }
    if (effectiveMethod === 'categories') {
      // Show categories items + manual redactions added while in categories mode
      return piiItems.filter(item =>
        (item.detectionMethod === 'categories' || item.detectionMethod === 'manual') &&
        (item.status === 'selected' || item.status === 'detected')
      );
    }
    // 'popia' or null: tagged popia + manual + legacy items without detectionMethod (excluding manual)
    return piiItems.filter(item =>
      item.detectionMethod === 'popia' ||
      item.detectionMethod === 'manual' ||
      (!item.detectionMethod && item.detectionType !== 'manual' && item.detectionType !== 'keyword')
    );
  })();

  // Get PII items for current page
  const currentPagePiiItems = filteredPiiItems.filter(item => item.page === currentPage);

  // Group PII items by page for "All Pages" view
  const piiItemsByPage = filteredPiiItems
    .sort((a, b) => a.page - b.page)
    .reduce((groups, item) => {
      const page = item.page || 1;
      if (!groups[page]) groups[page] = [];
      groups[page].push(item);
      return groups;
    }, {});

  // Group high-frequency PII items for "All Pages" view
  const { groupedItems, ungroupedItems } = (() => {
    const groupMap = {};
    const ungrouped = [];

    for (const item of filteredPiiItems) {
      const isHighFreq = item.highFrequency === true || (item.occurrenceCount || 0) >= 5;
      if (!isHighFreq) {
        ungrouped.push(item);
        continue;
      }
      const key = `${(item.value || '').toLowerCase()}|||${(item.category || '').toUpperCase()}`;
      if (!groupMap[key]) {
        groupMap[key] = { key, items: [] };
      }
      groupMap[key].items.push(item);
    }

    // Move groups with < 2 items back to ungrouped
    const groups = [];
    for (const group of Object.values(groupMap)) {
      if (group.items.length < 2) {
        ungrouped.push(...group.items);
      } else {
        groups.push(group);
      }
    }

    // Sort groups by count descending; ungrouped by page ascending
    groups.sort((a, b) => b.items.length - a.items.length);
    ungrouped.sort((a, b) => (a.page || 1) - (b.page || 1));

    return { groupedItems: groups, ungroupedItems: ungrouped };
  })();

  // Group ungrouped items by page for rendering
  const ungroupedByPage = ungroupedItems.reduce((groups, item) => {
    const page = item.page || 1;
    if (!groups[page]) groups[page] = [];
    groups[page].push(item);
    return groups;
  }, {});

  // Get color for PII category
  const getPiiColor = (category) => {
    // Check categories color map first (snake_case keys like 'email_addresses')
    const catColor = getCategoryColor(category);
    if (catColor.border !== '#9e9e9e') return catColor; // Not the default

    // Fall back to POPIA color map (UPPERCASE keys like 'EMAIL')
    const normalizedCategory = category?.toUpperCase().replace(/[^A-Z_]/g, '_');
    return PII_COLORS[normalizedCategory] || PII_COLORS.DEFAULT;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!documentData) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-gray-600">Document not found</p>
          <Link to="/dashboard" className="text-emerald-600 hover:text-emerald-700 mt-4 inline-block">
            Back to Dashboard
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const backButton = (
    <button
      onClick={() => navigate(-1)}
      className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
    >
      <ArrowLeft className="w-4 h-4" />
      Back
    </button>
  );

  return (
    <DashboardLayout breadcrumb={backButton}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base font-bold text-gray-900 truncate">{documentData.name}</h1>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>{filteredPiiItems.length} PII items detected</span>
                  <span>•</span>
                  <span>{numPages || '?'} pages</span>
                  {documentData.status && (
                    <>
                      <span>•</span>
                      <span className="capitalize">{documentData.status}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 ml-4">
              {documentData?.status === 'error' && (
                <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg dark:text-red-300 dark:bg-red-900/20 dark:border-red-800">
                  <AlertCircle className="w-4 h-4" />
                  Error — please retry
                </div>
              )}
              <button
                onClick={() => setShowDetectionWizard(true)}
                disabled={isAnalyzing || isProcessing || isCategoriesProcessing}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCategoriesProcessing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Detecting...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    Detection Methods
                  </>
                )}
              </button>

              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        </div>

        {/* Manual Redaction Toolbar */}
        <ManualRedactionToolbar
          onKeywordSearch={handleKeywordSearch}
          onToggleDrawMode={setDrawMode}
          drawMode={drawMode}
          isSearching={isSearching}
        />

        {/* Processing Status - shows during analyzing/processing */}
        {(documentData.status === 'analyzing' || documentData.status === 'processing') && (
          <div className="mt-4">
            <ProcessingStatus document={documentData} />
          </div>
        )}

        <div className="flex gap-6 mt-4">
          {/* PDF Viewer */}
          <div className="flex-1 relative bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Controls */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage <= 1}
                  className="p-2 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {numPages || '?'}
                </span>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage >= (numPages || 1)}
                  className="p-2 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRedactionPreview(prev => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    redactionPreview
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  title={redactionPreview ? 'Disable redaction preview' : 'Preview redacted document'}
                >
                  {redactionPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {redactionPreview ? 'Preview On' : 'Preview'}
                </button>

                <span className="text-gray-300 mx-1">|</span>

                <button
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  className="p-2 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 w-16 text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={zoomIn}
                  disabled={scale >= 2.5}
                  className="p-2 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* PDF Content */}
            <div className="overflow-auto bg-gray-100 p-4" style={{ maxHeight: 'calc(100vh - 300px)' }}>
              <div
                className="relative inline-block mx-auto"
                style={{
                  cursor: drawMode ? 'crosshair' : 'default',
                  ...(drawMode && { touchAction: 'none', userSelect: 'none' }),
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
              >
                {pdfLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                    <span className="ml-3 text-gray-600">Loading PDF...</span>
                  </div>
                ) : pdfError ? (
                  <div className="text-center p-8 text-red-600">
                    {pdfError}
                  </div>
                ) : pdfFile ? (
                <Document
                  file={pdfFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onPassword={(callback, reason) => {
                    // Suppress react-pdf's native window.prompt() and use our PasswordPromptModal instead
                    pdfPasswordCallbackRef.current = callback;
                    setShowPasswordPrompt(true);
                    if (reason === 2) {
                      setPasswordError('Incorrect password. Please try again.');
                    }
                  }}
                  loading={
                    <div className="flex items-center justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                    </div>
                  }
                  error={
                    <div className="text-center p-8 text-red-600">
                      Failed to render PDF. Please try again.
                    </div>
                  }
                >
                  <Page
                    pageNumber={currentPage}
                    scale={scale}
                    onLoadSuccess={onPageLoadSuccess}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
                ) : (
                  <div className="text-center p-8 text-gray-500">
                    No PDF available
                  </div>
                )}

                {/* PII Overlays - only show when PDF is loaded */}
                {pdfFile && currentPagePiiItems.map((item) => (
                  item.boundingBoxes?.map((box, boxIndex) => {
                    const color = getPiiColor(item.category);
                    // Bounding boxes are normalized {x0, y0, x1, y1} between 0-1
                    const left = box.x0 * pageDimensions.width * scale;
                    const top = box.y0 * pageDimensions.height * scale;
                    const width = (box.x1 - box.x0) * pageDimensions.width * scale;
                    const height = (box.y1 - box.y0) * pageDimensions.height * scale;

                    return (
                      <div
                        key={`${item.id}-${boxIndex}`}
                        className={`absolute ${redactionPreview ? '' : 'cursor-pointer'} transition-all`}
                        style={redactionPreview ? {
                          left: `${left}px`,
                          top: `${top}px`,
                          width: `${width}px`,
                          height: `${height}px`,
                          backgroundColor: '#000000',
                          border: 'none',
                          opacity: 1,
                        } : {
                          left: `${left}px`,
                          top: `${top}px`,
                          width: `${width}px`,
                          height: `${height}px`,
                          backgroundColor: hoveredPii === item.id ? color.border : color.bg,
                          border: `2px solid ${color.border}`,
                          opacity: hoveredPii === item.id ? 0.6 : 0.8,
                        }}
                        onMouseEnter={redactionPreview ? undefined : () => setHoveredPii(item.id)}
                        onMouseLeave={redactionPreview ? undefined : () => setHoveredPii(null)}
                        title={redactionPreview ? undefined : `${item.category}: ${item.value}`}
                      />
                    );
                  })
                ))}

                {/* Drawing rectangle overlay - show while actively drawing */}
                {drawMode && isDrawing && drawStart && drawCurrent && (
                  <div
                    className="absolute border-2 border-dashed border-blue-500 bg-blue-200 bg-opacity-30 pointer-events-none"
                    style={{
                      left: `${Math.min(drawStart.x, drawCurrent.x) * pageDimensions.width * scale}px`,
                      top: `${Math.min(drawStart.y, drawCurrent.y) * pageDimensions.height * scale}px`,
                      width: `${Math.abs(drawCurrent.x - drawStart.x) * pageDimensions.width * scale}px`,
                      height: `${Math.abs(drawCurrent.y - drawStart.y) * pageDimensions.height * scale}px`,
                    }}
                  />
                )}
              </div>
            </div>

            {/* Categories detection loading overlay */}
            {isCategoriesProcessing && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 rounded-lg">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg px-8 py-6 flex flex-col items-center gap-4 text-center">
                  <Loader className="w-10 h-10 animate-spin text-emerald-500" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">Detecting Categories...</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Scanning document for personal information</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar: Categories, Manual, or POPIA */}
          {effectiveMethod === 'categories' ? (
            <CategoriesSidebar
              documentId={documentId}
              userId={currentUser.uid}
              categorySummary={documentData.detections?.categories?.categorySummary || {}}
              hoveredPii={hoveredPii}
              setHoveredPii={setHoveredPii}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          ) : effectiveMethod === 'manual' ? (
          /* Manual Sidebar — flat page groups, no high-frequency section, no legend */
          <div className="w-80 bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-semibold text-gray-900">Manual Redactions</h2>
              </div>
              <div className="flex rounded-lg bg-gray-200 p-0.5">
                <button
                  onClick={() => setShowAllPages(false)}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                    !showAllPages
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  This Page
                </button>
                <button
                  onClick={() => setShowAllPages(true)}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                    showAllPages
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  All Pages
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {showAllPages
                  ? `${filteredPiiItems.length} items across all pages`
                  : `${currentPagePiiItems.length} items on this page`}
              </p>
            </div>

            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 380px)' }}>
              {showAllPages ? (
                filteredPiiItems.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No manual redactions yet. Use the toolbar above to draw or search.
                  </div>
                ) : (
                  Object.entries(piiItemsByPage).map(([page, items]) => (
                    <div key={page}>
                      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200">
                        <span className="text-xs font-semibold text-gray-700">Page {page}</span>
                        <span className="text-xs text-gray-500">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {items.map((item) => (
                          <PiiSidebarItem
                            key={item.id}
                            item={item}
                            color={getPiiColor(item.category)}
                            hoveredPii={hoveredPii}
                            setHoveredPii={setHoveredPii}
                            onDelete={handleDeletePii}
                            onClick={() => setCurrentPage(Number(page))}
                            showPageNav={Number(page) !== currentPage}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )
              ) : (
                currentPagePiiItems.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No manual redactions on this page
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {currentPagePiiItems.map((item) => (
                      <PiiSidebarItem
                        key={item.id}
                        item={item}
                        color={getPiiColor(item.category)}
                        hoveredPii={hoveredPii}
                        setHoveredPii={setHoveredPii}
                        onDelete={handleDeletePii}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
          ) : (
          /* POPIA Sidebar */
          <div className="w-80 bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-semibold text-gray-900">Detected PII</h2>
              </div>
              <div className="flex rounded-lg bg-gray-200 p-0.5">
                <button
                  onClick={() => setShowAllPages(false)}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                    !showAllPages
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  This Page
                </button>
                <button
                  onClick={() => setShowAllPages(true)}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                    showAllPages
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  All Pages
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {showAllPages
                  ? `${filteredPiiItems.length} items across all pages`
                  : `${currentPagePiiItems.length} items on this page`}
              </p>
            </div>

            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 380px)' }}>
              {showAllPages ? (
                filteredPiiItems.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No PII detected in this document
                  </div>
                ) : (
                  <>
                    {groupedItems.length > 0 && (
                      <>
                        <div className="sticky top-0 z-10 px-4 py-2 bg-amber-50 border-b border-amber-200">
                          <span className="text-xs font-semibold text-amber-700 uppercase">High Frequency</span>
                          <span className="text-xs text-amber-600 ml-2">{groupedItems.length} group{groupedItems.length !== 1 ? 's' : ''}</span>
                        </div>
                        {groupedItems.map((group) => (
                          <PiiGroupedItem
                            key={group.key}
                            group={group}
                            getPiiColor={getPiiColor}
                            hoveredPii={hoveredPii}
                            setHoveredPii={setHoveredPii}
                            onDeleteAll={handleKeepAll}
                            onDeleteSingle={handleDeletePii}
                            setCurrentPage={setCurrentPage}
                            currentPage={currentPage}
                          />
                        ))}
                      </>
                    )}
                    {Object.entries(ungroupedByPage).map(([page, items]) => (
                      <div key={page}>
                        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200">
                          <span className="text-xs font-semibold text-gray-700">Page {page}</span>
                          <span className="text-xs text-gray-500">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {items.map((item) => (
                            <PiiSidebarItem
                              key={item.id}
                              item={item}
                              color={getPiiColor(item.category)}
                              hoveredPii={hoveredPii}
                              setHoveredPii={setHoveredPii}
                              onDelete={handleDeletePii}
                              onClick={() => setCurrentPage(Number(page))}
                              showPageNav={Number(page) !== currentPage}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )
              ) : (
                currentPagePiiItems.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No PII detected on this page
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {currentPagePiiItems.map((item) => (
                      <PiiSidebarItem
                        key={item.id}
                        item={item}
                        color={getPiiColor(item.category)}
                        hoveredPii={hoveredPii}
                        setHoveredPii={setHoveredPii}
                        onDelete={handleDeletePii}
                      />
                    ))}
                  </div>
                )
              )}
            </div>

            <div className="border-t border-gray-200 dark:border-dark-border p-3">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Legend</h3>
              <div className="grid grid-cols-2 gap-1">
                {Object.entries(PII_COLORS)
                  .filter(([key]) => !['DEFAULT', 'MANUAL', 'NAMES', 'EMAILS', 'ADDRESSES', 'IDENTIFIER', 'SSN'].includes(key))
                  .map(([key, value]) => (
                    <div key={key} className="flex items-center gap-1.5">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: value.border }}
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                        {key.toLowerCase().replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Password Prompt Modal */}
      <PasswordPromptModal
        isOpen={showPasswordPrompt}
        onClose={() => {
          setShowPasswordPrompt(false);
          setPasswordError('');
        }}
        onSubmit={handlePasswordSubmit}
        isLoading={isUnlocking}
        error={passwordError}
      />

      {/* Document Analysis Modal */}
      {analysisResult && (
        <DocumentAnalysisModal
          isOpen={showAnalysisModal}
          document={{
            ...documentData,
            analysis: analysisResult
          }}
          userCredits={userData?.credits || 0}
          onConfirm={handleConfirmProcess}
          onCancel={() => {
            setShowAnalysisModal(false);
            setAnalysisResult(null);
          }}
        />
      )}

      {/* Detection Wizard Modal */}
      <DetectionWizardModal
        isOpen={showDetectionWizard}
        onClose={() => setShowDetectionWizard(false)}
        onSelectMethod={handleWizardSelect}
        document={documentData}
        userCredits={userData?.credits || 0}
        isProcessing={isCategoriesProcessing}
        isAnalyzing={isAnalyzing}
      />

      {/* Download Settings Modal */}
      <DownloadSettingsModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        documentId={documentId}
        documentName={documentData?.name}
        activeMethod={effectiveMethod}
      />

    </DashboardLayout>
  );
}
